import { type LimitsTier, type QuotaSource, type UserLimits } from '@chartai/types';

import { PLAN_ORDER } from './entitlements';

/**
 * CAB-603 — client-side parsing for `users/{uid}/limits/current`.
 *
 * Pure functions only. The hook (`useUserLimits`) wires Firestore to these; all
 * the decisions that can be got wrong live here, where they are testable.
 *
 * The governing rule throughout: FAIL CLOSED. A missing document, a malformed
 * field, or a value we don't recognise resolves to free tier with the free
 * allowance — never to unlimited. Mirrors `fallbackLimits()` on the backend,
 * because the server is what actually enforces this and the UI showing a more
 * generous number than the server will honour is worse than showing none.
 */

/** Free-tier allowance. Mirrors DEFAULT_QUOTA_MINUTES in the backend. */
export const FREE_QUOTA_MINUTES = 250;

const TIERS: readonly LimitsTier[] = ['free', 'basic', 'pro', 'ultimate'];
const SOURCES: readonly QuotaSource[] = ['stripe', 'default', 'manual'];

/**
 * Coerce a Firestore Timestamp / Date / millis / ISO string into a Date.
 * Returns null rather than an Invalid Date, so callers can branch cleanly.
 */
export function toDate(value: unknown): Date | null {
  if (value == null) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && 'toDate' in value) {
    const toDateFn = (value as { toDate: unknown }).toDate;
    if (typeof toDateFn === 'function') {
      const candidate: unknown = toDateFn.call(value);
      return candidate instanceof Date && !Number.isNaN(candidate.getTime()) ? candidate : null;
    }
    return null;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    const candidate = new Date(value);
    return Number.isNaN(candidate.getTime()) ? null : candidate;
  }
  return null;
}

function toTier(value: unknown): LimitsTier {
  return typeof value === 'string' && TIERS.includes(value as LimitsTier)
    ? (value as LimitsTier)
    : 'free';
}

function toSource(value: unknown): QuotaSource {
  return typeof value === 'string' && SOURCES.includes(value as QuotaSource)
    ? (value as QuotaSource)
    : 'default';
}

/**
 * A synthesised free-tier record, for when no document exists.
 *
 * Users who predate the CAB-603 backfill have none. Showing them "unlimited"
 * would be both wrong and the opposite of what the server enforces.
 */
export function fallbackLimits(): UserLimits {
  return {
    tier: 'free',
    quotaMinutes: FREE_QUOTA_MINUTES,
    secondsUsed: 0,
    periodStart: null,
    periodEnd: null,
    recordingBlocked: false,
    blockedAt: null,
    quotaSource: 'default',
    updatedAt: null,
    synthesised: true,
  };
}

/**
 * Parse the raw Firestore document into a `UserLimits`.
 *
 * `quotaMinutes` is the field that matters. It is unlimited ONLY when the
 * stored value is genuinely `null`; anything missing or malformed falls back to
 * the free allowance. Conflating "we couldn't read it" with "no limit" is the
 * one mistake here that costs money.
 */
export function parseLimits(raw: Record<string, unknown> | null | undefined): UserLimits {
  if (!raw) return fallbackLimits();

  const rawQuota = raw['quotaMinutes'];
  const quotaMinutes =
    rawQuota === null
      ? null
      : typeof rawQuota === 'number' && Number.isFinite(rawQuota)
        ? rawQuota
        : FREE_QUOTA_MINUTES;

  const rawSeconds = raw['secondsUsed'];
  const secondsUsed =
    typeof rawSeconds === 'number' && Number.isFinite(rawSeconds) ? Math.max(0, rawSeconds) : 0;

  return {
    tier: toTier(raw['tier']),
    quotaMinutes,
    secondsUsed,
    periodStart: toDate(raw['periodStart']),
    periodEnd: toDate(raw['periodEnd']),
    recordingBlocked: raw['recordingBlocked'] === true,
    blockedAt: toDate(raw['blockedAt']),
    quotaSource: toSource(raw['quotaSource']),
    updatedAt: toDate(raw['updatedAt']),
  };
}

/**
 * CAB-605 — is this user currently barred from starting a recording?
 *
 * **Derived, deliberately — never read `limits.recordingBlocked` for this.**
 * That field is a snapshot written when usage was last recorded, and the
 * Firestore rule does not consult it either. Reading it here would put the UI
 * and the server out of step in the worst direction: a user whose period has
 * since rolled would be shown a lockout the server would happily allow.
 *
 * This mirrors `underMinuteCap()` in `env/*​/firestore.rules` clause for clause.
 * If one changes, change the other:
 *
 *   1. unlimited plan            → never blocked
 *   2. period already expired    → never blocked (the block self-expires, which
 *                                  is what stops a blocked user deadlocking —
 *                                  usage only resets when a job COMPLETES, and
 *                                  a blocked user cannot complete one)
 *   3. otherwise                 → blocked once usage reaches the allowance
 *
 * @param limits Parsed limits document.
 * @param now    Injectable clock, for tests.
 */
export function isRecordingBlocked(limits: UserLimits, now: Date = new Date()): boolean {
  if (limits.quotaMinutes === null) return false;
  if (limits.periodEnd !== null && now.getTime() > limits.periodEnd.getTime()) return false;
  return limits.secondsUsed >= limits.quotaMinutes * 60;
}

/**
 * CAB-606 — fraction of the allowance at which the upgrade nudge appears.
 *
 * A constant rather than a literal so product can tune it without a rewrite;
 * 75% is the agreed starting point.
 */
export const NUDGE_THRESHOLD = 0.75;

/**
 * The plan a user would move to next, or null when there is nowhere to go.
 *
 * Only meaningful for the quota-bearing tiers — Pro and Ultimate are unlimited,
 * so the nudge never reaches them.
 */
export function nextPlanUp(tier: LimitsTier): LimitsTier | null {
  const index = PLAN_ORDER.indexOf(tier);
  if (index < 0) return null;
  return (PLAN_ORDER[index + 1] as LimitsTier | undefined) ?? null;
}

/**
 * CAB-606 — should this user be nudged to upgrade?
 *
 * Four ways to answer no, and each one exists for a reason:
 *
 *   1. **Unlimited plan** — nothing to run out of.
 *   2. **Period already expired** — the counter is stale and resets on the next
 *      recording, so warning about it would be wrong.
 *   3. **Already blocked** — at 100% the blocked state owns the message. AC5
 *      requires the two never show together, and "you're approaching your
 *      limit" next to "you've hit your limit" reads as a bug.
 *   4. **Below the threshold** — the ordinary case.
 *
 * @param limits Parsed limits document.
 * @param now    Injectable clock, for tests.
 */
export function shouldNudgeUpgrade(limits: UserLimits, now: Date = new Date()): boolean {
  if (limits.quotaMinutes === null) return false;
  if (limits.periodEnd !== null && now.getTime() > limits.periodEnd.getTime()) return false;
  if (isRecordingBlocked(limits, now)) return false;
  return deriveUsage(limits).fractionUsed >= NUDGE_THRESHOLD;
}

export interface UsageSummary {
  usedMinutes: number;
  /** Minutes left, or null when the plan is unlimited. */
  remainingMinutes: number | null;
  isUnlimited: boolean;
  /** Fraction of the allowance consumed, 0–1. Always 0 when unlimited. */
  fractionUsed: number;
}

/**
 * Derive the display figures from a parsed limits document.
 *
 * Note `remainingMinutes` floors at zero: a user who has overshot sees "0 left",
 * not a negative number. The overshoot is real — usage is recorded after a
 * recording completes, so the last one can cross the line — but surfacing it as
 * "-12 minutes remaining" reads as a bug.
 */
export function deriveUsage(limits: UserLimits): UsageSummary {
  const isUnlimited = limits.quotaMinutes === null;
  const usedMinutes = Math.round(limits.secondsUsed / 60);

  if (isUnlimited) {
    return { usedMinutes, remainingMinutes: null, isUnlimited: true, fractionUsed: 0 };
  }

  const quota = limits.quotaMinutes ?? 0;
  const remainingMinutes = Math.max(0, quota - usedMinutes);
  // A zero quota is fully consumed, not undefined — guard the division.
  const fractionUsed = quota > 0 ? Math.min(1, usedMinutes / quota) : 1;

  return { usedMinutes, remainingMinutes, isUnlimited: false, fractionUsed };
}
